/**
 * @file        src/pages/platform/TenantPicker.jsx
 * @description The tenant selector shared by every Tenant Management section
 *              page (Brands, Payment Gateways, Supplier Credentials, Admin
 *              Portal Branding). The chosen tenant id lives in the URL
 *              (?tenant=<id>) so the Tenant List / edit page can deep-link a
 *              section with the tenant preselected, refreshes survive, and
 *              switching sections keeps the selection. Suspended/pending
 *              tenants are listed too — configuring a tenant BEFORE it goes
 *              live is the whole point of the onboarding workflow.
 * @author      Kinetika Labs
 * @created     2026-08-10
 * @changelog
 *   - 2026-08-10 Claude — Initial (Tenant Management section pages).
 */
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Select from '../../components/Select';
import { get } from '../../api/client';
import { qk } from '../../api/queryKeys';

/** The ?tenant= selection: [tenantId (number|null), setTenantId]. */
export function useTenantParam() {
  const [params, setParams] = useSearchParams();
  const raw = Number(params.get('tenant'));
  const tenantId = Number.isInteger(raw) && raw > 0 ? raw : null;
  const setTenantId = (id) => setParams(id ? { tenant: String(id) } : {}, { replace: true });
  return [tenantId, setTenantId];
}

const LIST_PARAMS = { pageSize: 200 };

export default function TenantPicker({ value, onChange }) {
  const { data, isLoading } = useQuery({
    queryKey: qk.tenants.list(LIST_PARAMS),
    queryFn: () => get('/tenants', { params: LIST_PARAMS }),
  });
  const rows = data?.data || [];
  const options = rows.map((t) => ({
    value: String(t.id),
    label: `${t.name} — ${t.domain}${t.status === 'active' ? '' : ` (${t.status})`}`,
  }));

  return (
    <div className="field" style={{ maxWidth: 420 }}>
      <label>Tenant</label>
      <Select
        value={value ? String(value) : ''}
        onChange={(v) => onChange(Number(v))}
        options={options}
        placeholder={isLoading ? 'Loading tenants…' : 'Select a tenant'}
      />
    </div>
  );
}
